import { endpoints, type PatientSearchResult } from '@api'
import { SearchIcon } from '@blueprinthq/joy'
import {
  Avatar,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react'
import { Add } from '@material-ui/icons'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useClinicianSchedulingControllerGetAddNewSuggestions } from '~/clinician-api'
import type { Client } from '../models'

interface ClientPickerProps {
  onSelect: (option: Client) => void
  onInputChange: (inputText: string) => void
  addEventClicked?: () => void
  addClientClicked?: () => void
  value: Client | null
  errors: any
}

interface Option {
  label: string
  value: string
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  icon?: React.ReactElement
  highlight: boolean
  fontColor: string
  selected: boolean
}

interface DebouncedInputReturn {
  inputText: string
  setInputText: (text: string) => void
  debouncedInputText: string
  isTyping: boolean
  setInputTextDebounced: (text: string) => void
}

interface SearchResultsReturn {
  searchResults: PatientSearchResult[] | undefined
  searchResultsLoading: boolean
  addNewSuggestions: any | undefined
  addNewSuggestionsLoading: boolean
}

const useDebouncedInput = (
  initialValue: string,
  delay: number = 300
): DebouncedInputReturn => {
  const [inputText, setInputText] = useState(initialValue)
  const [debouncedInputText, setDebouncedInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  const setInputTextDebounced = useRef(
    debounce((inputText: string) => setDebouncedInputText(inputText), delay)
  ).current

  useEffect(() => {
    if (!inputText) {
      setIsTyping(false)
      return
    }

    setIsTyping(true)
    const timer = setTimeout(() => {
      setIsTyping(false)
    }, delay)
    return () => clearTimeout(timer)
  }, [inputText, delay])

  return {
    inputText,
    setInputText,
    debouncedInputText,
    isTyping,
    setInputTextDebounced
  }
}

const useSearchResults = (searchText: string): SearchResultsReturn => {
  const { data: searchResults, isLoading: searchResultsLoading } = useQuery(
    [
      endpoints.getPatientSearch.getCacheId(),
      'session_client_search',
      searchText
    ],
    () =>
      endpoints.getPatientSearch.request({
        search: searchText,
        include_discharged: false
      }),
    {
      select: (data: PatientSearchResult[]) =>
        data.filter((n: PatientSearchResult) => !n.is_demo)
    }
  )

  const { data: addNewSuggestions, isLoading: addNewSuggestionsLoading } =
    useClinicianSchedulingControllerGetAddNewSuggestions(
      {
        q: searchText
      },
      {
        query: {
          enabled: searchText.length > 4
        }
      }
    )

  return {
    searchResults,
    searchResultsLoading,
    addNewSuggestions,
    addNewSuggestionsLoading
  }
}

export const ClientPicker: React.FC<ClientPickerProps> = ({
  onSelect,
  onInputChange,
  addEventClicked,
  addClientClicked,
  value,
  errors
}) => {
  const [activeIndex, setActiveIndex] = useState(-1)
  const [hasResults, setHasResults] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    inputText,
    setInputText,
    debouncedInputText,
    isTyping,
    setInputTextDebounced
  } = useDebouncedInput(
    value?.id ? `${value?.firstName} ${value?.lastName}` : ''
  )

  const {
    searchResults,
    searchResultsLoading,
    addNewSuggestions,
    addNewSuggestionsLoading
  } = useSearchResults(debouncedInputText)

  const handleItemClicked = useCallback(
    (item: Option) => {
      setShowDropdown(false)
      if (item.value === 'add-event') {
        setInputText('')
        addEventClicked?.()
      } else if (item.value === 'add-client') {
        setInputText('')
        addClientClicked?.()
      } else {
        onSelect({
          firstName: item.firstName || '',
          lastName: item.lastName || '',
          email: item.email,
          phoneNumber: item.phoneNumber,
          id: item.value
        })
        setInputText(item.label)
      }
    },
    [addEventClicked, addClientClicked, onSelect, setInputText]
  )

  const onSearch = useCallback(
    (inputText: string) => {
      setInputText(inputText)
      setInputTextDebounced(inputText)
      onInputChange(inputText)
    },
    [setInputText, setInputTextDebounced, onInputChange]
  )

  useEffect(() => {
    setHasResults(!!searchResults && searchResults.length > 0)
  }, [searchResults])

  const allOptions: Option[] = useMemo(() => {
    if (debouncedInputText.length === 0) {
      return []
    }

    const options: Option[] = [
      ...(searchResults || []).slice(0, 3).map(client => ({
        value: client.id,
        firstName: client.first_name,
        lastName: client.last_name,
        email: client.email,
        phoneNumber: client.phone_number,
        label: `${client.first_name} ${client.last_name}`,
        highlight: false,
        fontColor: 'black',
        selected: false
      }))
    ]

    let highlightedOption: string | null = null
    if (!hasResults) highlightedOption = 'add-client'

    if (addNewSuggestions?.eventType === 'ClientSession') {
      highlightedOption = 'add-client'
    }

    if (addNewSuggestions?.eventType === 'OtherEvent') {
      highlightedOption = 'add-event'
    }

    if (!hasResults) {
      options.push({
        label: 'Save event',
        value: 'add-event',
        icon: <Add />,
        highlight: highlightedOption === 'add-event',
        fontColor: 'primary',
        selected: highlightedOption === 'add-event'
      })
    }

    options.push({
      label: 'Add client',
      value: 'add-client',
      icon: <Add />,
      highlight: highlightedOption === 'add-client',
      fontColor: 'primary',
      selected: !hasResults && highlightedOption === 'add-client'
    })
    return options
  }, [searchResults, debouncedInputText, hasResults, addNewSuggestions])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(prev => Math.min(prev + 1, allOptions.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        handleItemClicked(allOptions[activeIndex])
      }
    },
    [handleItemClicked, allOptions, activeIndex]
  )

  const showLoading =
    searchResultsLoading || addNewSuggestionsLoading || isTyping

  return (
    <>
      <InputGroup>
        <InputLeftElement>
          <SearchIcon name="search" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search or add client or event..."
          border="none"
          borderBottom="1px solid rgba(55, 60, 65, 0.14)"
          _focus={{
            outline: 'none',
            borderBottom: '1px solid var(--chakra-colors-primary)'
          }}
          fontSize="24px"
          onKeyDown={handleKeyDown}
          onChange={e => onSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          value={inputText}
        />
        {showLoading && (
          <InputRightElement>
            <Spinner
              size="sm"
              color="primary"
              position="absolute"
              right="10px"
            />
          </InputRightElement>
        )}
      </InputGroup>
      {!showLoading &&
        !hasResults &&
        showDropdown &&
        debouncedInputText.length > 0 && (
          <Text color="#757575" padding="12px 3px">
            No client found
          </Text>
        )}
      {!showLoading && showDropdown && (
        <VStack gap="8px" width="100%" alignItems="flex-start">
          {allOptions.map((item, index) => (
            <Flex
              key={item.value}
              alignItems="center"
              bg={index === activeIndex ? '#F6F7FE' : 'transparent'}
              p="2px"
              w="100%"
              borderRadius="16px 8px 8px 16px"
              cursor="pointer"
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(-1)}
              onClick={() => handleItemClicked(item)}
            >
              <Avatar
                size="sm"
                name={item.icon ? undefined : item.label}
                icon={item.icon ? item.icon : undefined}
                sx={{
                  marginRight: 'small',
                  background: item.highlight
                    ? 'var(--chakra-colors-primary)'
                    : ' rgba(45, 84, 232, 0.04)',
                  color: item.highlight ? 'white' : 'primary',
                  fontWeight: 'bold'
                }}
              />
              <Text color={item.fontColor}>{item.label}</Text>
            </Flex>
          ))}
        </VStack>
      )}
    </>
  )
}
